import * as React from 'react';
import * as autoBind from 'react-autobind';
import {ControlButton, TscButtonType, TextInput} from '@techsmith/tsc-cloud-style-guide';
import ManageReviewersContainer from '../ManageReviewers/ManageReviewersContainer';
import Constants from '../../globals/Constants';
import Strings from '../../globals/Strings';
import ReviewerGroupModel from '../../types/ReviewerGroupModel';
import SimpleReviewerModel from '../../types/SimpleReviewerModel';
import FeedbackUserService from '../../services/FeedbackUserService/FeedbackUserService';
import Themeable from '../../types/Themeable';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import UserMessaging from '../../utils/UserMessaging';

export default class AddReviewerGroup extends React.Component<AddReviewerGroupProps, AddReviewerGroupState> {
   readonly state: AddReviewerGroupState = {
      isSubmitting: false,
      addingReviewers: [],
      removingReviewers: [],
      groupName: this.props.reviewerGroup.GroupName,
      saveButtonClicked: false,
      errorsCanShow: false
   };

   constructor(props: AddReviewerGroupProps) {
      super(props);
      autoBind(this);
   }

   onCancel(): void {
      this.setState({isSubmitting: false});
      this.props.onCancel();
   }

   async onClickSave(): Promise<void> {
      const {reviewerGroup} = this.props;
      const {groupName, addingReviewers} = this.state;
      this.setState({isSubmitting: true, saveButtonClicked: true});

      if (reviewerGroup.Id) {
         try {
            await this.saveReviewerGroup();
            this.onCancel();
         } catch {
            UserMessaging.warn(Strings.errors.failedUpdateReviewsGroup);
            this.setState({isSubmitting: false, saveButtonClicked: false});
         }
      } else {
         try {
            await FeedbackUserService.addReviewerGroup({GroupName: groupName.trim(), ReviewerEmailAddressesToAdd: addingReviewers.map(r => r.Email)});
            this.onCancel();
         } catch {
            UserMessaging.warn(Strings.errors.failedCreateReviewersGroup);
            this.setState({isSubmitting: false, saveButtonClicked: false});
         }
      }
   }

   saveReviewerGroup(): Promise<any> {
      const {reviewerGroup} = this.props;
      const {groupName, addingReviewers, removingReviewers} = this.state;

      let allPromises = [];

      // Update changed group name
      if (groupName !== reviewerGroup.GroupName) {
         allPromises.push(FeedbackUserService.updateReviewerGroupName(reviewerGroup.Id, groupName.trim()));
      }

      // Remove deleted reviewers
      allPromises = allPromises.concat(removingReviewers.map(reviewer => FeedbackUserService.deleteReviewerFromGroup(reviewerGroup.Id, reviewer.Id)));

      // Adding reviewers
      allPromises = allPromises.concat(addingReviewers.map(reviewer => FeedbackUserService.addReviewerToGroup(reviewerGroup.Id, reviewer.Email)));

      return Promise.all(allPromises);
   }

   onNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
      this.setState({groupName: (event.target as HTMLInputElement).value, errorsCanShow: true});
   }

   clearAddingReviewersWithEmails(emails: string[]): void {
      const {addingReviewers} = this.state;
      const newAddingReviewers = addingReviewers.filter(addingReviewer => !emails.includes(addingReviewer.Email));
      this.setState({addingReviewers: newAddingReviewers});
   }

   clearRemovingReviewersWithEmails(emails: string[]): void {
      const {removingReviewers} = this.state;
      this.setState({removingReviewers: removingReviewers.filter(removingReviewer => !emails.includes(removingReviewer.Email))});
   }

   addReviewers(reviewersToAdd: SimpleReviewerModel[]): void {
      const {addingReviewers} = this.state;
      const uniqueReviewersToAdd = reviewersToAdd.filter(r => !this.props.reviewerGroup.GroupMembers.some(gm => gm.Email === r.Email) && !addingReviewers.some(ar => ar.Email === r.Email));
      this.clearRemovingReviewersWithEmails(reviewersToAdd.map(r => r.Email));
      this.setState({errorsCanShow: true, addingReviewers: this.state.addingReviewers.concat(uniqueReviewersToAdd)});
   }

   showGroupEmailsError() {
      const {errorsCanShow, addingReviewers} = this.state;
      return !this.props.reviewerGroup.Id && errorsCanShow && addingReviewers.length === 0;
   }

   removeReviewer(reviewer: SimpleReviewerModel): Promise<void> {
      const {addingReviewers, removingReviewers} = this.state;

      if (removingReviewers.some(r => r.Id === reviewer.Id)) {
         return;
      }

      if (addingReviewers.some(r => r.Email === reviewer.Email)) {
         this.clearAddingReviewersWithEmails([reviewer.Email]);
         return;
      }

      this.setState({removingReviewers: this.state.removingReviewers.concat(Object.assign({}, reviewer, {Status: Constants.reviewerStatus.removing}))});
   }

   cancelRemoveReviewer(reviewer: SimpleReviewerModel): void {
      this.setState({removingReviewers: this.state.removingReviewers.filter(r => r.Id !== reviewer.Id)});
   }

   getReviewers(): SimpleReviewerModel[] {
      const {addingReviewers, removingReviewers} = this.state;

      const reviewers: SimpleReviewerModel[] = this.props.reviewerGroup.GroupMembers.filter(gm => !removingReviewers.some(r => gm.Email === r.Email)).map(groupUserModel => {
         return {Email: groupUserModel.Email, Id: groupUserModel.TscId, Status: Constants.reviewerStatus.added, Notify: true};
      });

      return reviewers.concat(addingReviewers).concat(removingReviewers);
   }

   hasPendingChanges(): boolean {
      const {groupName, addingReviewers, removingReviewers, saveButtonClicked} = this.state;
      const {reviewerGroup} = this.props;

      const hasGroupNameChanged = reviewerGroup.GroupName && reviewerGroup.GroupName !== groupName;
      const isChangingReviewers = addingReviewers.length > 0 || removingReviewers.length > 0;
      const hasChanges = hasGroupNameChanged || isChangingReviewers;
      return saveButtonClicked ? false : hasChanges;
   }

   componentDidUpdate() {
      this.props.setHasPendingChanges(this.state.isSubmitting ? false : this.hasPendingChanges());
   }

   getInputErrorText(): string {
      const {reviewerGroup, reviewerGroups} = this.props;
      const {groupName, errorsCanShow} = this.state;
      const trimmedGroupName = groupName.trim();

      if (!trimmedGroupName && errorsCanShow) {
         return Strings.errors.invalidGroupName;
      }

      if (reviewerGroup.GroupName !== trimmedGroupName && reviewerGroups.some(g => g.GroupName.toLowerCase() === trimmedGroupName.substr(0, Constants.groupName.maxLength).toLowerCase())) {
         return Strings.errors.groupNameExists;
      }

      if (trimmedGroupName.length > Constants.groupName.maxLength) {
         return Strings.errors.invalidGroupNameLength.replace('{param0}', `${Constants.groupName.maxLength}`);
      }
   }

   render(): React.ReactElement<any> {
      const {onCancel, reviewerGroup, themeName} = this.props;
      const {isSubmitting, groupName} = this.state;
      const inputErrorText = this.getInputErrorText();
      const reviewers: SimpleReviewerModel[] = this.getReviewers();
      const primaryButtonDisabled = !groupName || !reviewers.length || !!inputErrorText || !this.hasPendingChanges();
      const primaryButtonText = reviewerGroup.Id ? Strings.labels.updateGroup : Strings.labels.createGroup;
      const reviewersErrorText = this.showGroupEmailsError() ? Strings.errors.invalidGroupEmails : '';

      return (
         <div className="review-modal-body invite-modal-container">
            {isSubmitting && <LoadingSpinner/>}
            <label htmlFor="reviewer-group-name-input" className="input-label">{Strings.labels.enterGroup}</label>
            <TextInput sharedId="reviewer-group-name-input"
                       themeName={themeName}
                       placeholder={Strings.labels.typeGroup}
                       onChange={this.onNameChange}
                       value={groupName}
                       errorText={inputErrorText}
                       testId="reviewer-group-name-input"/>
            <ManageReviewersContainer showInput={true}
                                      prompt={Strings.labels.enterReviewers}
                                      reviewers={reviewers}
                                      isOwner={true}
                                      removeReviewer={this.removeReviewer}
                                      addReviewers={this.addReviewers}
                                      errorText={reviewersErrorText}
                                      showRemoveReviewerText={false}
                                      cancelRemoveReviewer={this.cancelRemoveReviewer}/>
            <div className="button-group">
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.secondary}
                  isFullWidth={true}
                  onClick={onCancel}
                  label={Strings.labels.cancel}
                  testId="cancel-button"
               />
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.primary}
                  isFullWidth={true}
                  onClick={this.onClickSave}
                  label={primaryButtonText}
                  testId="create-group-button"
                  disabled={primaryButtonDisabled}
               />
            </div>
         </div>
      );
   }
}

export interface AddReviewerGroupProps extends Themeable {
   reviewerGroup: ReviewerGroupModel;
   reviewerGroups: ReviewerGroupModel[];
   onCancel(): void;
   setHasPendingChanges(hasPendingChanges: boolean): void;
}

export interface AddReviewerGroupState {
   isSubmitting: boolean;
   saveButtonClicked: boolean;
   groupName: string;
   addingReviewers: SimpleReviewerModel[];
   removingReviewers: SimpleReviewerModel[];
   errorsCanShow: boolean;
}
